import { UploadSimpleIcon } from "@phosphor-icons/react/dist/csr/UploadSimple";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { formatMoneyCents } from "@/core/utils/money";
import type { StAdjustmentPushIntent } from "./types";

// The scope line — the thing a reviewer most needs to see before money moves. A
// single-technician push names the person, because "Technician #430353700" does
// not tell anyone whose pay is about to leave.
export const describePushScope = (intent: StAdjustmentPushIntent): string => {
  if (intent.technicianIds === null)
    return "Every technician with lines pending";

  const count = intent.technicianIds.length;
  if (count === 1) {
    const id = `#${intent.technicianIds[0]}`;
    // The id rides along even when named, so the confirmation matches what the
    // reviewer would look up in ServiceTitan.
    return intent.technicianName
      ? `${intent.technicianName} (${id})`
      : `Technician ${id}`;
  }

  return `${count} selected technicians`;
};

/**
 * Last stop before adjustments leave for ServiceTitan.
 *
 * A push writes to an external payroll system and cannot be recalled, and the
 * "Push all" path can send an entire period's adjustments from one click — so the
 * dialog states the scope, the line count and the money, and makes the user say
 * yes to those specific numbers.
 *
 * The copy stays neutral: pushing is the ordinary thing this tab is for, and
 * dressing the confirmation up as a warning would train people to click through
 * it. What earns attention is the figures, not alarm.
 */
export const StAdjustmentPushConfirmationDialog = ({
  open,
  intent,
  isPushing,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  // Null while nothing is staged — the dialog is closed in that state.
  intent: StAdjustmentPushIntent | null;
  isPushing: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) => (
  <Dialog
    open={open && !!intent}
    onOpenChange={(nextOpen) => {
      // Not while the request is in flight: closing would strand the user with no
      // idea whether the push went out.
      if (!nextOpen && !isPushing) onOpenChange(false);
    }}
  >
    {/* Wider than the 449px default so the scope line and the figures sit on one
        row each rather than wrapping into cramped columns. */}
    <DialogContent className="sm:max-w-xl" showCloseButton={!isPushing}>
      <DialogHeader>
        <DialogTitle>Push with PushPay?</DialogTitle>
        <DialogDescription className="sr-only">
          Confirm the PushPay payroll adjustment push
        </DialogDescription>
      </DialogHeader>
      {intent && (
        <Stack align="start" gap={4}>
          <Stack
            align="start"
            direction="row"
            gap={6}
            className="w-full rounded-lg border border-line bg-neutral px-4 py-3.5"
          >
            <Stack align="start" gap={1} className="min-w-0 flex-1">
              <Text
                variant="caption"
                className="font-medium tracking-widest text-brand uppercase"
              >
                Scope
              </Text>
              <Text variant="label">{describePushScope(intent)}</Text>
            </Stack>
            <Stack align="start" gap={1} className="shrink-0">
              <Text
                variant="caption"
                className="font-medium tracking-widest text-brand uppercase"
              >
                Lines
              </Text>
              <Text variant="label" className="tabular-nums">
                {intent.lineCount}
              </Text>
            </Stack>
            <Stack align="start" gap={1} className="shrink-0">
              <Text
                variant="caption"
                className="font-medium tracking-widest text-brand uppercase"
              >
                Amount
              </Text>
              <Text variant="label" className="tabular-nums">
                {formatMoneyCents(intent.amountCents) ?? "$0"}
              </Text>
            </Stack>
          </Stack>
          <Text tone="muted">
            PushPay writes these adjustments as payroll for the period. You
            cannot undo them here. Lines that already landed are skipped, so a
            second push never pays twice.
          </Text>
        </Stack>
      )}
      <DialogFooter>
        <Button
          variant="secondary"
          onClick={() => onOpenChange(false)}
          disabled={isPushing}
        >
          Cancel
        </Button>
        <Button
          disabled={isPushing}
          onClick={onConfirm}
          aria-busy={isPushing || undefined}
        >
          {isPushing ? (
            <Spinner data-icon="inline-start" aria-hidden />
          ) : (
            <UploadSimpleIcon data-icon="inline-start" className="h-4 w-4" />
          )}
          {intent ? `Push ${intent.lineCount}` : "Push"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
